import { Component, OnDestroy, output } from '@angular/core';
import { Subscription, take } from 'rxjs';
import { HandoffInfo } from 'src/app/common/models/handoffInfo';
import { FacilityManager } from 'src/app/managers/facility.manager';

import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { StoreHandoffService } from '@src/app/services/store-handoff.service';

@Component({
  selector: 'multi-facility-selection',
  templateUrl: './multi-facility-selection.component.html',
  styleUrls: ['./multi-facility-selection.component.scss'],
  standalone: true,
  imports: [ReactiveFormsModule, MatProgressSpinner, MatSelectModule, MatFormFieldModule],
})
export class MultiFacilitySelectionComponent implements OnDestroy {
  private cachedHandoffInfo: Map<string, HandoffInfo> = new Map();
  public currentFacilityId: string;
  public selectedFacilities = new FormControl<string[]>([]);
  public get selectedFacilitiesCount(): number {
    return this.selectedFacilities.value?.length || 0;
  }
  public facilities: HandoffInfo[] = [];
  public errorMessage: any;
  public facilitiesLoaded = false;

  private _subscriptions: Subscription[] = [];

  readonly storesSelected = output<HandoffInfo[]>();

  constructor(
    private facilityManager: FacilityManager,
    private storeHandoffService: StoreHandoffService,
  ) {
    const facilityIdUpdateSubscription = this.facilityManager.facilityIdUpdates$.subscribe({
      next: (fId) => {
        this.selectedFacilities.setValue([fId]);
        this.cachedHandoffInfo = new Map();
        this.currentFacilityId = fId;
      },
    });
    const facilitiesLoadedSubscription = this.facilityManager.facilitiesLoaded$.subscribe({
      next: (_event) => {
        this.facilities = this.facilityManager.storeHandoffInfo();
        this.facilitiesLoaded = true;
      },
    });
    this._subscriptions.push(facilityIdUpdateSubscription, facilitiesLoadedSubscription);
    this.currentFacilityId = this.facilityManager.getCurrentFacility();
    this.selectedFacilities.setValue([this.currentFacilityId]);
  }

  public ngOnDestroy(): void {
    this._subscriptions.forEach((s) => {
      s.unsubscribe();
    });
  }

  public storeSelected(): void {
    const selectedFacilities = this.selectedFacilities.value;
    const didRemove = this.removeDeselectedStores(this.cachedHandoffInfo, new Set(selectedFacilities));
    if (didRemove) {
      this.storesSelected.emit([...this.cachedHandoffInfo.values()]);
    } else {
      selectedFacilities?.forEach((f) => {
        if (this.cachedHandoffInfo.has(f)) return;
        this.storeHandoffService
          .getStoreHandoffInformation(f)
          .pipe(take(1))
          .subscribe((facility) => {
            const handoffInfo = facility;
            if (handoffInfo) {
              this.cachedHandoffInfo.set(f, handoffInfo);
            }
            this.storesSelected.emit([...this.cachedHandoffInfo.values()]);
          });
      });
    }
  }

  private removeDeselectedStores(cachedHandoffInfo: Map<string, HandoffInfo>, selectedIds: Set<string>): boolean {
    let removeCount = 0;
    for (const [facilityId, _handoffInfo] of cachedHandoffInfo) {
      if (!selectedIds.has(facilityId)) {
        cachedHandoffInfo.delete(facilityId);
        removeCount++;
      }
    }
    return removeCount > 0;
  }
}
